<template>
  <BaseContainer>
    <BaseFlexSpinner v-if="state.isLoading" class="mt-12" />
    <div v-else class="relative px-5 pt-3 pb-24">
      <div class="flex flex-row">
        <div
          v-if="program?.teasing_content && state.videos.length"
          class="bg-orange-light rounded-lg shadow p-4 cursor-pointer z-20 flex flex-1"
          :class="[{'mb-4': state.videos.length === 0, 'mb-0': state.videos.length > 0}]"
        >
          <div class="flex flex-1 flex-col">
            <p class="flex flex-1 text-primary font-bold">
              Plus pour vos patients dans l'application Apodis !
            </p>
            <p class="flex flex-1 text-primary">
              {{ program?.teasing_content }}
            </p>
          </div>
        </div>
        <div v-if="state.videos?.length" class="w-[215px]" />
      </div>
      <Dialog v-if="state.videos?.length">
        <DialogTrigger>
          <div class="absolute top-[105px] transform transition hover:-translate-y-1 right-10 z-10">
            <div
              class="relative cursor-pointer"
              @mouseover="state.discoverAppMessage = 'Cliquez pour voir en plein écran !'"
              @mouseleave="state.discoverAppMessage = 'Découvrir l\'app !'"
            >
              <p class="absolute m-auto left-0 right-0 text-primary text-center text-sm mt-[-34px]">
                {{ state.discoverAppMessage }}
              </p>
              <div class="absolute m-auto left-0 right-0 w-[115px] h-[245px] mt-[3px]">
                <video
                  :src="state.videos[0].url"
                  loop
                  autoplay
                  muted
                  class="object-contain"
                  style="image-rendering: auto"
                />
              </div>
              <img
                alt="iphone-mockup"
                :src="TransparentIphoneMockup"
                class="h-[280px] -mt-[70px] object-contain select-none drop-shadow-md"
              >
            </div>
          </div>
        </DialogTrigger>
        <DialogScrollContent>
          <div class="relative inline-block max-w-sm mt-12 mx-auto">
            <img
              alt="iphone-mockup"
              src="@/assets/images/iphone-mockup-transparent.png"
              class="w-full object-contain pointer-events-none"
            >

            <video
              :src="state.videos[0].url"
              loop
              autoplay
              muted
              style="image-rendering: auto"
              class="select-none object-contain absolute h-[91.7%] w-[84%] left-[calc(50%)] -translate-x-1/2 top-[2.8%] rounded-[1rem]"
            />
          </div>
        </DialogScrollContent>
      </Dialog>
      <BaseCard class="relative z-20 overflow-hidden">
        <ProgramRefreshProgressBar v-if="state.isRefreshing" />
        <div class="flex flex-col gap-2 p-5">
          <div>
            <div class="flex flex-1 flex-row items-center space-between">
              <div class="flex flex-1 flex-col">
                <div class="flex items-center gap-2">
                  <h1 class="flex  text-gray-dark">
                    Patients
                  </h1>
                  <ProgramDataRefresh
                    v-if="!state.enableSelection"
                    label="Actualiser la liste des patients"
                    date-prefix="Liste du"
                    :cached-at="state.meta?.cached_at"
                    :loading="state.isRefreshing"
                    :refreshed-at="state.refreshedAt"
                    @refresh="refreshConsumers"
                  />
                </div>
                <div v-if="hiddenConsumers.length > 0">
                  <p
                    class="border-l border-gray-300 pl-2 text-sm cursor-pointer mt-1"
                    @click="state.disabledConsumersModalVisible = true"
                  >
                    {{ hiddenConsumers.length }}
                    <span v-if="hiddenConsumers.length > 1">patients masqués </span>
                    <span v-else>patient masqué </span>
                    <span class="underline">Voir la liste</span>
                  </p>
                  <ProgramExcludedConsumersModal
                    v-if="program.programmable_id"
                    :visible="state.disabledConsumersModalVisible"
                    :consumers="hiddenConsumers"
                    :program="program"
                    @close="settingsModalClose"
                    @unmasked="state.unmaskedConsumers.push($event)"
                  />
                </div>
              </div>
              <div class="max-w-[75%] flex gap-4 items-center">
                <router-link v-if="!state.enableSelection" :to="{name: 'program.details.conversation-messages', params: {programId: program.id}}" class="shrink-0">
                  <div class="flex items-center text-primary group mt-2 shrink-0">
                    <History class="size-5 mr-1 shrink-0" />
                    <span class="leading-tight font-medium group-hover:underline">Historique des envois</span>
                  </div>
                </router-link>
                <CommunicationBanner
                  class="items-end"
                  :selection-enabled="state.enableSelection"
                  @on-press="onBannerPress"
                  @on-confirm="confirmSelection"
                  @on-cancel="state.enableSelection = false"
                />
              </div>
            </div>
          </div>
          <div
            v-if="state.meta"
            class="flex flex-col bg-slate-100 px-5 py-3 gap-3"
          >
            <div class="flex justify-between items-center gap-4">
              <h3 class="flex-1">
                Filtres
              </h3>
              <!-- The communication selection only works on the list. -->
              <div
                v-if="!state.enableSelection"
                class="flex shrink-0 rounded-full bg-white shadow-md p-0.5"
                role="group"
                aria-label="Affichage des patients"
              >
                <button
                  v-for="option in VIEW_OPTIONS"
                  :key="option.value"
                  type="button"
                  class="flex items-center gap-1.5 h-7 px-3 rounded-full text-sm font-medium transition-colors duration-200"
                  :class="state.view === option.value ? 'bg-primary text-white' : 'text-gray-dark hover:text-primary'"
                  :aria-pressed="state.view === option.value"
                  @click="setView(option.value)"
                >
                  <component :is="option.icon" class="size-4" />
                  {{ option.label }}
                </button>
              </div>
              <div class="min-w-72 max-w-96 w-full">
                <input
                  v-model="state.consumerFilter"
                  type="search"
                  class="w-full h-8 text-sm border-gray-100 rounded-md shadow-md focus:ring-indigo-500 focus:border-indigo-500 placeholder-gray-300"
                  placeholder="Rechercher un patient par son nom ..."
                >
              </div>
            </div>
            <div class="flex">
              <div v-if="!state.meta.options.hideGroups" class="flex flex-1 flex-wrap gap-5">
                <button
                  class="text-sm font-semibold"
                  :class="!state.groupFilter ? 'text-primary': ''"
                  @click="setGroupFilter(null)"
                >
                  Tous les groupes
                </button>
                <button
                  v-for="(item) in Object.entries(allowedInclusionFilters)"
                  :key="item[0]"
                  class="text-sm font-semibold"
                  :class="state.groupFilter === item[0] ? 'text-primary': ''"
                  @click="setGroupFilter(item[0])"
                >
                  {{ item[1] }}
                </button>
              </div>
              <div>
                <BaseSelect
                  v-if="state.meta"
                  v-model:model-value="state.exclusionFilter"
                  default-value=""
                  trigger-class="border-none h-6 bg-transparent flex justify-end"
                  placeholder="Tous les statuts"
                  :options="[
                    {value: 'all', label: 'Tous les statuts'},
                    ...Object.keys(state.meta.excluded).map((key) => ({value: key, label: state.meta!.excluded[key]})),
                    ...Object.keys(state.meta.not_excluded).map((key) => ({value: key, label: state.meta!.not_excluded[key]}))
                  ]"
                />
              </div>
            </div>
          </div>
          <SupportProgramGroupConsumersTable
            v-if="state.meta && (state.view === 'list' || state.enableSelection)"
            :consumers="filteredConsumers"
            :meta="state.meta"
            :enable-selection="state.enableSelection"
            @consumer-click="showCustomer"
          />
          <ProgramFollowUpTimeline
            v-else-if="state.meta"
            class="mt-2"
            :program="program"
            :consumers="filteredConsumers"
            :meta="state.meta"
            :groups="allowedInclusionFilters"
            @open="showCustomer"
          />
          <div v-else-if="state.loadFailed" class="flex flex-col items-center gap-3 py-10 text-center">
            <p class="text-base font-semibold text-gray-dark">
              La liste des patients n'a pas pu être chargée
            </p>
            <p class="text-sm text-slate-500">
              Vérifiez votre connexion et réessayez.
            </p>
            <Button variant="outline" size="sm" class="border-solid" @click="loadPage">
              <RotateCw />
              Réessayer
            </Button>
          </div>
        </div>
      </BaseCard>
    </div>
  </BaseContainer>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive} from "vue";
import type {Component} from "vue";
import BaseContainer from '@/core/components/base/BaseContainer.vue';
import {programApi, videoApi} from '@/container';
import BaseCard from '@/core/components/base/BaseCard.vue';
import BaseFlexSpinner from '@/core/components/base/spinner/BaseFlexSpinner.vue';
import SupportProgramGroupConsumersTable from '@/program/components/program-consumers/SupportProgramConsumerTable.vue';
import ProgramDataRefresh from '@/program/components/program-refresh/ProgramDataRefresh.vue';
import ProgramRefreshProgressBar from '@/program/components/program-refresh/ProgramRefreshProgressBar.vue';
import ProgramExcludedConsumersModal from '@/program/components/program-consumers/ProgramExcludedConsumersModal.vue';
import {Program, SupportProgramMeta, SupportProgramPatient} from "@/program/interfaces/Program";
import CommunicationBanner from "@/communication/components/CommunicationBanner.vue";
import {useCommunicationStore} from "@/communication/stores/communication.store";
import {useToastStore} from "@/stores/toast.store";
import BaseSelect from "@/core/components/base/BaseSelect.vue";
import ConsumerKeyService from "@/consumer/services/ConsumerKeyService";
import {useRoute, useRouter} from "vue-router";
import TransparentIphoneMockup from "@/assets/images/iphone-mockup-transparent.png";
import {Dialog, DialogScrollContent, DialogTrigger} from "@/core/components/ui/dialog";
import {Video} from "@/video/interfaces";
import useSupportProgramSettings from "@/program/composables/useSupportProgramSettings";
import SupportProgramSettingsService from "@/program/services/SupportProgramSettingsService";
import {ChartGantt, History, List, RotateCw} from "@lucide/vue";
import ProgramFollowUpTimeline from "@/program/components/program-follow-up/ProgramFollowUpTimeline.vue";
import {Button} from "@/core/components/ui/button";

const router = useRouter();
const route = useRoute();

const props = defineProps<{
    program: Program
}>();

type ConsumersView = 'list' | 'timeline';

const VIEW_OPTIONS: { value: ConsumersView, label: string, icon: Component }[] = [
    {value: 'list', label: 'Liste', icon: List},
    {value: 'timeline', label: 'Timeline', icon: ChartGantt},
];

/**
 * Switch between the list and the timeline of the patients. The page always opens on the list.
 */
const setView = (view: ConsumersView): void => {
    state.view = view;
};

const state = reactive({
    isLoading: false,
    loadFailed: false,
    consumers: [] as SupportProgramPatient[],
    consumerFilter: null as string | null,
    groupFilter: null as string | null,
    exclusionFilter: null as string | null,
    meta: null as SupportProgramMeta | null,
    disabledConsumersModalVisible: false,
    enableSelection: false,
    videos: [] as Video[],
    unmaskedConsumers: [] as SupportProgramPatient[],
    discoverAppMessage: "Découvrir l'app !",
    isRefreshing: false,
    refreshedAt: null as number | null,
    view: 'list' as ConsumersView
});

const {data} = useSupportProgramSettings(props.program.programmable_id);
const communicationStore = useCommunicationStore();
const toastStore = useToastStore();

const allowedInclusionFilters = computed(() => {
    const requiredGroups = state.meta ? Object.keys(state.meta.included).filter((slug) => {
        if (!state.meta?.optional_groups) {
            return true;
        }

        return state.meta!.optional_groups?.includes(slug) === false;
    }) : [];

    const enabledOptionalGroups = state.meta ? state.meta.optional_groups?.filter((group) => {
        return SupportProgramSettingsService.isInclusionGroupEnabled(data.value!, group);
    }) : [];

    const allowedKeys = [...requiredGroups, ...(enabledOptionalGroups || [])];

    const allowed: { [key: string]: string } = {};
    allowedKeys.forEach((key) => {
        if (state.meta && state.meta.included[key]) {
            allowed[key] = state.meta.included[key];
        }
    });

    return allowed;
});

/**
 * Get the filtered customers.
 */
const filteredConsumers = computed(() => {
    return state.consumers
        .filter((consumer: SupportProgramPatient) => {
            const inclusionGroups = consumer.groups.included.map(group => group.slug);
            return Object.keys(allowedInclusionFilters.value).some(slug => inclusionGroups.includes(slug));
        })
        .filter((consumer: SupportProgramPatient) =>
            !state.consumerFilter || consumer.name.toLowerCase().includes(state.consumerFilter.toLowerCase()))
        .filter(consumer => {
            const inclusionGroups = consumer.groups.included.map(group => group.slug);

            return !state.groupFilter || (!!state.groupFilter && inclusionGroups.includes(state.groupFilter));
        })
        .filter(consumer => {
            if (!state.exclusionFilter || state.exclusionFilter == 'all') {
                return true;
            }

            const exclusionGroups = consumer.groups.excluded.map(group => group.slug);
            const filter = state.exclusionFilter.match(/(not:)?(.*)/i);
            const includes = exclusionGroups.includes(filter![2]);

            return filter![1] === 'not:' ? !includes : includes;
        })
        .filter(consumer => !isHidden(consumer));
});

/**
 * Check if the consumer is hidden.
 * @param consumer
 */
const isHidden = (consumer: SupportProgramPatient) => {
    return (!!consumer.feedbacks.find(feedback => feedback.hide_notification)
            || !!consumer.settings.find(setting => setting.disabled))
        && !state.unmaskedConsumers.find(unmasked => unmasked.key === consumer.key);
};

/**
 * Get the hidden consumers.
 */
const hiddenConsumers = computed(() => {
    return state.consumers.filter(consumer => isHidden(consumer));
});

/**
 * Load the support program consumers.
 * @param supportProgramId
 */
const loadConsumers = async (supportProgramId: number): Promise<void> => {
    const data = await programApi().consumers(supportProgramId);
    state.consumers = data.data;
    state.meta = data.meta;
};

/**
 * Recompute the program patients then reload the list, to take into account
 * the latest data such as newly reconciled patients. The dedicated refreshing
 * flag keeps the table readable during the call, filters and selection
 * included.
 */
const refreshConsumers = async (): Promise<void> => {
    if (state.isRefreshing || !props.program.programmable_id) {
        return;
    }

    state.isRefreshing = true;

    try {
        await programApi().refreshConsumers(props.program.programmable_id);
    } catch (error) {
        const status = (error as { response?: { status?: number } }).response?.status;

        if (status !== 429) {
            state.isRefreshing = false;
            toastStore.showError({
                content: 'Impossible d\'actualiser les données. Vérifiez votre connexion et réessayez.'
            });

            return;
        }
    }

    try {
        await loadConsumers(props.program.programmable_id);
        state.refreshedAt = Date.now();
    } catch {
        toastStore.showError({
            content: 'Impossible d\'actualiser les données. Vérifiez votre connexion et réessayez.'
        });
    } finally {
        state.isRefreshing = false;
    }
};

/**
 * Load promotional videos linked to the support program.
 */
const loadVideos = async (supportProgramId: number): Promise<void> => {
    const data = await videoApi().index({owner_id: supportProgramId, owner_type: "support", tags: ['promotional']});

    state.videos = data.data;
};

/**
 * Set the program group filter.
 *
 * @param slug
 */
const setGroupFilter = (slug: string | null) => {
    state.groupFilter = slug;
};

/**
 * Show the customer details.
 *
 * @param consumer
 */
const showCustomer = (consumer: SupportProgramPatient) => {
    let customerId: number | undefined;
    const consumerKey = ConsumerKeyService.defaultKey(consumer.key);
    const programId = props.program.id;

    if (ConsumerKeyService.isBeneficiary(consumerKey)) {
        customerId = consumer.customer_id;
    } else {
        customerId = parseInt(consumerKey.slice(2));
    }

    router.push({
        name: 'consumer.support-program',
        params: {
            customerId,
            consumerKey,
            programId
        }
    });
};

/**
 * On settings modal close, set the visible state to false and refresh the settings.
 */
const settingsModalClose = () => {
    state.disabledConsumersModalVisible = false;
};

/**
 * On communication banner press.
 */
const onBannerPress = () => {
    state.enableSelection = true;
};

/**
 * Confirm the selection of consumers.
 */
const confirmSelection = () => {
    if (state.consumers) {
        if (communicationStore.programCustomers.length > 0) {
            const filteredCustomers = state.consumers
                .filter(consumer => communicationStore.programCustomers.includes(consumer.key));

            communicationStore.customers = filteredCustomers.map(customer => {
                return {
                    id: Number(customer.key.replace("c_", "")),
                    name: customer.name,
                    phone: customer.phone,
                    birthdate: customer.birthdate
                };
            });

            communicationStore.programId = props.program.id;
            communicationStore.mode = 'sms';
            communicationStore.from = {name: route.name as string, params: route.params};
            router.push({name: 'patient-administration.communication.message.sms'});
        } else {
            toastStore.showInfo({content: 'Vous devez sélectionner au moins un patient pour continuer'});
        }
    }
};

/**
 * Load the consumers then the promotional videos behind the page spinner.
 * A consumers failure (timeout, 5xx, network) swaps the table for a retry
 * state instead of leaving the spinner up; the videos fail silently.
 */
const loadPage = async (): Promise<void> => {
    state.isLoading = true;
    state.loadFailed = false;

    try {
        try {
            await loadConsumers(props.program.programmable_id);
        } catch {
            state.loadFailed = true;
            toastStore.showError({
                content: 'Impossible de charger les patients. Vérifiez votre connexion et réessayez.'
            });
        }

        try {
            await loadVideos(props.program.programmable_id);
        } catch {
            //
        }
    } finally {
        state.isLoading = false;
    }
};

/**
 * On mounting, load the consumers.
 */
onMounted(async () => {
    communicationStore.$reset();

    await loadPage();
});
</script>
